import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserStore } from '@/store/userStore';

export type AppRole = 'admin' | 'coach' | 'client' | 'super_admin';

interface UseUserRolesResult {
  roles: AppRole[];
  isLoading: boolean;
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
  isCoach: boolean;
  isClient: boolean;
  isSuperAdmin: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch user roles from user_roles table
 * Uses the new user_roles system instead of is_admin/is_coach flags
 */
export function useUserRoles(userId?: string, gymId?: string): UseUserRolesResult {
  const { gymId: currentGymId } = useUserStore();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRoles = async () => {
    if (!userId) {
      setRoles([]);
      setIsLoading(false);
      return;
    }

    const targetGymId = gymId || currentGymId;
    if (!targetGymId) {
      setRoles([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('gym_id', targetGymId);

      if (error) throw error;

      const userRoles = (data || []).map(r => r.role as AppRole);
      setRoles(userRoles);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [userId, gymId, currentGymId]);

  const hasRole = (role: AppRole) => roles.includes(role);

  return {
    roles,
    isLoading,
    hasRole,
    isAdmin: hasRole('admin'),
    isCoach: hasRole('coach'),
    isClient: hasRole('client'),
    isSuperAdmin: hasRole('super_admin'),
    refetch: fetchRoles
  };
}

/**
 * Helper function to determine user role for navigation
 * Returns the highest priority role: super_admin > admin > coach > client
 */
export function getPrimaryRole(roles: AppRole[]): 'admin' | 'coach' | 'athlete' | 'super_admin' {
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('coach')) return 'coach';
  return 'athlete'; // client maps to athlete for navigation
}
